import {Button, Col, DatePicker, Form, Input, message, Modal, Row, Space, Table, Tag} from "antd";
import {useEffect, useState} from "react";
import {ExclamationCircleOutlined, RetweetOutlined} from "@ant-design/icons";
import {NOT_AVAILABLE, YYYY_MM_DD, YYYY_MM_DD_HH_MM} from "../../../util/Constants";
import {
    MSG_FAILED_TO_FETCH_RE_SYNC_TABLE_LIST_ERROR,
    MSG_TABLE_RESYNC_PROCESS_START_FAILURE,
    MSG_TABLE_RESYNC_PROCESS_STARTED_SUCCESS
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import {TableColumnsType} from "antd/lib/table";
import modal from "antd/es/modal";
import adminService from "../../../service/adminService";
import dayjs from "dayjs";
import {SyncTableDto} from "../../../dto/admin/SyncTableDto";
import Validation from "../../../util/Validation";
import {TransactionTableResyncDto} from "../../../dto/admin/TransactionTableResyncDto";

const {RangePicker} = DatePicker;

const TableResync = () => {
    const [resyncTableList, setResyncTableList] = useState<SyncTableDto[]>([]);
    const [isSpinning, setSpinning] = useState(false);
    const [isCanOpenModal, setCanOpenModal] = useState(false);


    const columns: TableColumnsType<SyncTableDto> = [
        {
            title: 'ID',
            dataIndex: 'id',
            render: (text: any, rec: any, index: number) => {
                return (<span>{index + 1}</span>)
            }
        },
        {
            title: 'Table Name',
            dataIndex: 'tableName',
            sorter: (a: any, b: any) => {
                a = a.tableName == null ? '' : a.tableName
                b = b.tableName == null ? '' : b.tableName
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Last Sync Datetime',
            dataIndex: 'lastSyncTime',
            key: 'lastSyncTime',
            sorter: (a: any, b: any) => {
                a = a.lastSyncTime == null ? '' : a.lastSyncTime
                b = b.lastSyncTime == null ? '' : b.lastSyncTime
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Processing',
            dataIndex: 'processing',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.customerNic == null ? '' : a.customerNic
                b = b.customerNic == null ? '' : b.customerNic
                return a.localeCompare(b);
            },
            render: (text: any, rec: any, index: number) => {
                return (getProcessingTag(text))
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            fixed: 'right' as const,
            render: (text: any, rec: any) => (
                <Space size="middle">
                    <Button className='btn-action-outline-common btn-action-re-schedule'
                            disabled={!rec.canResync}
                            icon={<RetweetOutlined/>}
                            onClick={!rec.apxTransaction ? onReSync : openModal}>Re-Sync</Button>
                </Space>
            ),
        },
    ];


    const getProcessingTag = (type: string) => {

        switch (type) {
            case "N":
                return <Tag color='green'>NO</Tag>
            case "Y":
                return <Tag color='red'>Yes</Tag>
            default:
                <Tag color='default'>N/A</Tag>
        }
    }
    const openModal = () => {
        setCanOpenModal(true)
    }

    useEffect(() => {
        fetchResyncTableList()
    }, []);
    const fetchResyncTableList = () => {
        setSpinning(true)
        adminService.fetchResyncTableList().then(res => {
            if (res.data !== null) {
                setResyncTableList(res.data.results)
                setSpinning(false)
            } else {
                message.error(MSG_FAILED_TO_FETCH_RE_SYNC_TABLE_LIST_ERROR).then();
                setSpinning(false)
            }

        }, (reason) => {
            ErrorHandler.handleError(reason);
            setSpinning(false)
        })
    }


    const onReSync = () => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: `Are you sure?, Do you want to re-sync this table`,
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {
                resyncTable();
            },
            onCancel() {
            }
        });

    }


    const resyncTable = () => {
        setSpinning(true)
        adminService.resyncTable().then((res) => {
            if (res.data.results) {
                message.success(MSG_TABLE_RESYNC_PROCESS_STARTED_SUCCESS)
                fetchResyncTableList();
                setSpinning(false)
            } else {
                message.error(MSG_TABLE_RESYNC_PROCESS_START_FAILURE)
                fetchResyncTableList();
                setSpinning(false)
            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
            fetchResyncTableList();
            setSpinning(false)

        })
    }

    const handleCancel = () => {
        setCanOpenModal(false)
    }

    const onFinish = (values: any) => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: `Are you sure?, Do you want to re-sync this table`,
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {
                const data = {
                    portfolioNo: values.portfolioNo,
                    fromDate: values.dateRange[0].format(YYYY_MM_DD),
                    toDate: values.dateRange[1].format(YYYY_MM_DD)
                } as TransactionTableResyncDto;
                setSpinning(true)
                handleCancel()
                adminService.resyncTransactionTable(data).then((res) => {
                    if (res.data.results) {
                        message.success(MSG_TABLE_RESYNC_PROCESS_STARTED_SUCCESS)
                        fetchResyncTableList();
                        setSpinning(false)
                    } else {
                        message.error(MSG_TABLE_RESYNC_PROCESS_START_FAILURE)
                        fetchResyncTableList();
                        setSpinning(false)
                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    fetchResyncTableList();
                    setSpinning(false)
                })
            },
            onCancel() {
            }
        });

    }
    return (
        <div>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'>
                    <h2 className='page-header-main-title'>
                        <div className='header_icon header-icon-center'>
                            <RetweetOutlined/>
                        </div>
                        Table Re-Sync
                    </h2>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className='margin_top_30'>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Table scroll={{x: 'max-content'}} columns={columns} dataSource={resyncTableList}
                           rowKey={(record: any) => record.id}
                           loading={isSpinning}/>

                </Col>

                <Modal title="Re-Sync Details"
                       open={isCanOpenModal}
                       destroyOnClose={true}
                       maskClosable={false}
                       footer={null}
                       onCancel={handleCancel}
                >
                    <Row>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Form
                                layout={"vertical"}
                                initialValues={{remember: true}}
                                onFinish={onFinish}
                            >
                                <Row gutter={[12, 0]}>
                                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name={"portfolioNo"}
                                            label={"Portfolio Number"}
                                            onKeyPress={Validation.isNumberWithDash}
                                            rules={[{
                                                required: true,
                                                message: 'Please enter portfolio number'
                                            }]}>
                                            <Input placeholder="Enter portfolio number"/>
                                        </Form.Item>
                                    </Col>
                                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            label="Date Range"
                                            name="dateRange"
                                            rules={[{
                                                required: true,
                                                message: 'Please enter date range'
                                            },
                                                () => ({
                                                    validator(_: any, value: any[]) {
                                                        if (!value || !value[0] || !value[1]) {
                                                            return Promise.resolve();
                                                        }
                                                        // Check if the 'To Date' is greater than 'From Date'
                                                        if (value[0] && value[1] && value[0].isAfter(value[1])) {
                                                            return Promise.reject(new Error('To Date should be greater than From Date'));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                }),]}>
                                            <RangePicker placeholder={["From Date", "To Date"]}
                                                         style={{width: '100%'}}/>

                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Space style={{float: 'right'}}>
                                        <Button htmlType="submit"
                                                type="primary">
                                            Re-Sync
                                        </Button>
                                    </Space>
                                </Form.Item>


                            </Form>
                        </Col>
                    </Row>

                </Modal>
            </Row>
        </div>


    )

}
export default TableResync;

